import { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Lock, User, Eye, EyeOff, ArrowLeft, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { MatchStakeIcon } from '../components/MatchStakeLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = () => {
  const { login, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');
  const [isRegister, setIsRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    name: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        // Validate email
        if (!formData.email || !formData.email.includes('@')) {
          toast.error('Please enter a valid email address');
          setLoading(false);
          return;
        }
        if (!formData.username || formData.username.trim().length < 3) {
          toast.error('Username must be at least 3 characters');
          setLoading(false);
          return;
        }
        if (formData.password.length < 8) {
          toast.error('Password must be at least 8 characters');
          setLoading(false);
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          setLoading(false);
          return;
        }
        
        const response = await axios.post(
          `${API}/auth/register`,
          {
            username: formData.username.trim().toLowerCase(),
            email: formData.email.trim().toLowerCase(),
            password: formData.password,
            confirm_password: formData.confirmPassword,
            name: formData.name?.trim() || null
          },
          { withCredentials: true }
        );
        
        // Store session token in localStorage as backup
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }
        
        setUser(response.data);
        toast.success('Account created! Please check your email to verify your account.');
        navigate('/verify-email-sent');
      } else {
        // Login with username
        if (!formData.username || !formData.password) {
          toast.error('Please enter username and password');
          setLoading(false);
          return;
        }
        
        const response = await axios.post(
          `${API}/auth/login`,
          {
            username: formData.username.trim().toLowerCase(),
            password: formData.password
          },
          { withCredentials: true }
        );
        
        // Store session token in localStorage as backup
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }
        
        setUser(response.data);
        toast.success('Welcome back!');
        navigate(returnUrl || '/dashboard');
      }
    } catch (error) {
      let errorMsg = 'Authentication failed. Please try again.';
      
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.message === 'Network Error') {
        errorMsg = 'Unable to connect to server. Please check your internet connection.';
      } else if (error.code === 'ERR_NETWORK') {
        errorMsg = 'Network error. Please try again.';
      }
      
      toast.error(errorMsg);
      console.error('Auth error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      
      {/* Custom styles to fix white autofill background and icon colors */}
      <style>{`
        input:-webkit-autofill,
        input:-webkit-autofill:hover, 
        input:-webkit-autofill:focus, 
        input:-webkit-autofill:active {
          -webkit-background-clip: text;
          -webkit-text-fill-color: white !important;
          transition: background-color 5000s ease-in-out 0s;
          box-shadow: inset 0 0 20px 20px #1a1a1a !important;
        }
      `}</style>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Back to Home */}
        <Link 
          to="/" 
          className="flex items-center gap-2 text-white/50 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Logo */}
        <div className="flex items-center gap-2 justify-center mb-8">
          <MatchStakeIcon className="w-12 h-12" />
          <span className="font-heading font-bold text-2xl tracking-tight">MATCHSTAKE</span>
        </div>

        {/* Card */}
        <div className="glass-card rounded-2xl p-8">
          <h1 className="font-heading text-2xl font-bold text-center mb-2">
            {isRegister ? 'Create Account' : 'Welcome Back'}
          </h1>
          <p className="text-white/50 text-center mb-8">
            {isRegister ? 'Join the competition' : 'Sign in to continue'}
          </p>

          {/* Username/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4 mb-6">
            {isRegister && (
              <>
                <div>
                  <label className="text-sm text-white/70 mb-1 block">Display Name <span className="text-white/40">(optional)</span></label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                    <Input
                      type="text"
                      placeholder="Your display name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pl-10 bg-white/5 border-white/10 focus:border-[#00F0FF]"
                      data-testid="register-name"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-white/70 mb-1 block">Email <span className="text-[#FF2E2E]">*</span></label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10 bg-white/5 border-white/10 focus:border-[#00F0FF]"
                      required
                      data-testid="register-email"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="text-sm text-white/70 mb-1 block">Username {isRegister && <span className="text-[#FF2E2E]">*</span>}</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                <Input
                  type="text"
                  placeholder={isRegister ? "Choose a username" : "Your username"}
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="pl-10 bg-white/5 border-white/10 focus:border-[#00F0FF]"
                  required
                  data-testid="login-username"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-white/70 mb-1 block">Password {isRegister && <span className="text-[#FF2E2E]">*</span>}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={isRegister ? 'Min 8 characters' : 'Your password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 pr-10 bg-white/5 border-white/10 focus:border-[#00F0FF]"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 z-10"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {isRegister && (
              <div>
                <label className="text-sm text-white/70 mb-1 block">Confirm Password <span className="text-[#FF2E2E]">*</span></label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="pl-10 pr-10 bg-white/5 border-white/10 focus:border-[#00F0FF]"
                    required
                    data-testid="register-confirm-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 z-10"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-6"
              data-testid="login-submit"
            >
              {loading ? 'Please wait...' : (isRegister ? 'Create Account' : 'Sign In')}
            </Button>
            
            {/* Forgot Password Link */}
            {!isRegister && (
              <div className="text-center mt-3">
                <Link to="/forgot-password" className="text-sm text-white/50 hover:text-[#00F0FF] transition-colors">
                  Forgot your password?
                </Link>
              </div>
            )}
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#0A0A0B] text-white/50">or continue with</span>
            </div>
          </div>

          {/* Google Login */}
          <Button
            onClick={login}
            variant="outline"
            className="w-full py-6 border-white/20 hover:bg-white/5"
            data-testid="google-login"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>

          {/* Toggle Register/Login */}
          <p className="text-center text-white/50 mt-6">
            {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="text-[#00F0FF] hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isRegister ? 'Sign In' : 'Create Account'}
            </button>
          </p>
        </div>

        {/* Terms */}
        <p className="text-center text-white/30 text-xs mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </motion.div>
    </div>
  );
};

export default Login;
